/**
 * Partnership Data Point Registry
 *
 * Canonical registry of data points for partnership-style agreements
 * (Template.kind === 'CONTRACT' with contractType in
 *  REFERRAL_BROKER / CORRESPONDENT_LENDING / WHOLESALE / CHANNEL_PARTNER).
 *
 * Surfaces:
 * - Designer ContextRail filters this list by `category` so admins can drag
 *   typed canonical keys into PDF / structured fields.
 * - Envelope Generate-time resolution — the partnership-agreement-context
 *   builder maps these keys to live values from CRM / Tenant before sending
 *   to DocuSeal.
 *
 * Multi-tenancy: this registry is platform-scoped (no businessId). A tenant
 * can override per-template field bindings, but the canonical-key vocabulary
 * is shared.
 *
 * Sensitive-key invariant (CLAUDE.md §2 + #1531 v4 §6.2): partnerships are
 * keyed off business EIN, not personal PII — `ssn`/`dob`/`credit_score` MUST
 * NOT appear in this list. The test suite enforces this.
 *
 * @see #1531 v4 §5 + §6.6 — per-kind canonical-key registries
 */

export interface PartnershipDataPoint {
  /** Unique canonical key used across ContextRail, designer field binding, and envelope generation */
  canonicalKey: string;
  /** Human-readable display name shown in the designer's data-point picker */
  label: string;
  /** Tooltip / help text describing what the value represents */
  description: string;
  /** Logical grouping for ContextRail accordion sections */
  category: 'partner' | 'lender' | 'agreement';
  /** Expected value type for runtime coercion at envelope-Generate time */
  type: 'string' | 'number' | 'date' | 'boolean';
  /** Whether the field MUST be resolved before the envelope can be sent */
  required: boolean;
}

export const PARTNERSHIP_DATA_POINTS: readonly PartnershipDataPoint[] = [
  // --------------------------------------------------------------------------
  // PARTNER side (the inviting business)
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'partner_legal_name',
    label: 'Partner Legal Name',
    description: 'Legal name of the partner business',
    category: 'partner',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'partner_business_address',
    label: 'Partner Business Address',
    description: 'Registered address',
    category: 'partner',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'partner_ein',
    label: 'Partner EIN',
    description: 'Federal Employer Identification Number',
    category: 'partner',
    type: 'string',
    required: false,
  },
  {
    canonicalKey: 'partner_email',
    label: 'Partner Email',
    description: 'Primary contact email',
    category: 'partner',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'partner_phone',
    label: 'Partner Phone',
    description: 'Primary contact phone',
    category: 'partner',
    type: 'string',
    required: false,
  },
  {
    canonicalKey: 'partner_signer_name',
    label: 'Partner Signer Name',
    description: 'Authorized signer for the partner business',
    category: 'partner',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'partner_signer_title',
    label: 'Partner Signer Title',
    description: 'Title of the partner signer',
    category: 'partner',
    type: 'string',
    required: false,
  },

  // --------------------------------------------------------------------------
  // LENDER side (the receiving business)
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'lender_legal_name',
    label: 'Lender Legal Name',
    description: 'Legal name of the lender business',
    category: 'lender',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'lender_signer_name',
    label: 'Lender Signer Name',
    description: 'Authorized signer for the lender business',
    category: 'lender',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'lender_signer_title',
    label: 'Lender Signer Title',
    description: 'Title of the lender signer',
    category: 'lender',
    type: 'string',
    required: false,
  },

  // --------------------------------------------------------------------------
  // AGREEMENT terms
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'agreement_type',
    label: 'Agreement Type',
    description: 'Referral / correspondent / wholesale / white_label',
    category: 'agreement',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'effective_date',
    label: 'Effective Date',
    description: 'When the agreement takes effect',
    category: 'agreement',
    type: 'date',
    required: true,
  },
  {
    canonicalKey: 'expiration_date',
    label: 'Expiration Date',
    description: 'When the agreement expires (null = perpetual)',
    category: 'agreement',
    type: 'date',
    required: false,
  },
  {
    canonicalKey: 'commission_tier',
    label: 'Commission Tier',
    description: 'Display name of the commission structure tier',
    category: 'agreement',
    type: 'string',
    required: false,
  },
  {
    canonicalKey: 'commission_rate',
    label: 'Commission Rate',
    description: 'Percentage rate (e.g., 0.025 = 2.5%)',
    category: 'agreement',
    type: 'number',
    required: false,
  },
];

/** Flat list of every canonical key in this registry — convenience for callers that just need string lookup. */
export const PARTNERSHIP_CANONICAL_KEYS: readonly string[] = PARTNERSHIP_DATA_POINTS.map((p) => p.canonicalKey);
